import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateMedium, formatDateTimeMedium, useUtcToday } from '@klareospace/pharos-lib-front-utils';
import { toUtcYesterday } from '@klareospace/pharos-lib-utils';
import { Tabs, TabsList, TabsTrigger } from '@klareospace/pharos-lib-web-utils';

import {
  DriverKpisMonth,
  DriverKpisToday,
  OrderKpisMonth,
  OrderKpisToday,
  type UseDriverKpisMonthResult,
  type UseDriverKpisTodayResult,
  type UseOrderKpisMonthResult,
  type UseOrderKpisTodayResult,
} from '&/features/kpis';

// ─── Constants ──────────────────────────────────────────────────────────────

export const DASHBOARD_TAB = {
  MONTH: 'month',
  TODAY: 'today',
} as const;
export type DashboardTab = (typeof DASHBOARD_TAB)[keyof typeof DASHBOARD_TAB];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DashboardProps {
  activeTab: DashboardTab;
  driverKpis: UseDriverKpisTodayResult;
  kpis: UseOrderKpisTodayResult;
  monthDriverKpis: UseDriverKpisMonthResult;
  monthOrderKpis: UseOrderKpisMonthResult;
  onActiveTabChange: (tab: DashboardTab) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isDashboardTab(value: string): value is DashboardTab {
  return (Object.values(DASHBOARD_TAB) as string[]).includes(value);
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Today | This month tab bar + content. Purely presentational — tab state and all data-fetching hooks are owned by `HomePage`. */
export function Dashboard({
  activeTab,
  driverKpis,
  kpis,
  monthDriverKpis,
  monthOrderKpis,
  onActiveTabChange,
}: DashboardProps): React.JSX.Element {
  const { i18n, t } = useTranslation('kpis');
  const utcToday = useUtcToday();
  const isMonthTab = activeTab === DASHBOARD_TAB.MONTH;
  const computedAt = isMonthTab ? monthOrderKpis.computedAt : kpis.computedAt;
  const yesterday = toUtcYesterday(utcToday);

  function handleTabValueChange(value: string): void {
    if (isDashboardTab(value)) {
      onActiveTabChange(value);
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-auto px-6 pb-6 pt-4">
      <div className="flex items-center justify-between">
        <div>
          <Tabs onValueChange={handleTabValueChange} value={activeTab}>
            <TabsList>
              <TabsTrigger className="cursor-pointer" value={DASHBOARD_TAB.TODAY}>
                {t('tabs.today')}
              </TabsTrigger>
              <TabsTrigger className="cursor-pointer" value={DASHBOARD_TAB.MONTH}>
                {t('tabs.month')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {isMonthTab && (
            <p className="text-xs text-muted-foreground">
              {t('monthPeriodLabel', { date: formatDateMedium(yesterday, i18n.language) })}
            </p>
          )}
        </div>
        {computedAt && (
          <p className="text-xs text-muted-foreground">
            {t('lastUpdated', { datetime: formatDateTimeMedium(computedAt, i18n.language) })}
          </p>
        )}
      </div>
      {isMonthTab ? (
        <div className="flex flex-col gap-6">
          <OrderKpisMonth
            isError={monthOrderKpis.isError}
            isLoading={monthOrderKpis.isLoading}
            metrics={monthOrderKpis.metrics}
          />
          <DriverKpisMonth
            isError={monthDriverKpis.isError}
            isLoading={monthDriverKpis.isLoading}
            rows={monthDriverKpis.rows}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <OrderKpisToday isError={kpis.isError} isLoading={kpis.isLoading} metrics={kpis.metrics} />
          {driverKpis.isEnabled && (
            <DriverKpisToday isError={driverKpis.isError} isLoading={driverKpis.isLoading} rows={driverKpis.rows} />
          )}
        </div>
      )}
    </div>
  );
}
